import { type ReactElement, type SyntheticEvent, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInfiniteQuery, useMutation } from "react-query"
import { useSelector } from "react-redux"

import CloseIcon from "@mui/icons-material/Close"
import { CircularProgress } from "@mui/material"
import Grid from "@mui/material/Grid"
import IconButton from "@mui/material/IconButton"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { InputText, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { getTheme } from "../../../../hooks/UseTheme"
import { useDebounce } from "../../../../hooks/useDebounce"
import { KonanAPI } from "../../../../services/KonanAPI"
import { queryClient } from "../../../../store/QueryClientStore"
import type { RootState } from "../../../../store/ReduxStore"
import type { Label } from "../../../../types/generated/api/Label"
import type { PaginatedLabelList } from "../../../../types/generated/api/PaginatedLabelList"
import { extractPageFromBackEndPaginationLink } from "../../../../utils/genericHelpers"
import type { ConfigurationsSliderProps } from "../../Interfaces"
import { BaseSlider } from "../../Slider/BaseSlider"

import styles from "./ConfigurationSlider.module.scss"

/**
 * ConfigurationSlider is a component that combines a label selector with a slider control.
 * It allows users to select or create labels and adjust corresponding slider values.
 *
 * @interface {Object} props - Component props
 * @param {{name: string; uuid: string}} label - The currently selected label object containing name and uuid
 * @param {number} min - Minimum value for the slider
 * @param {number} max - Maximum value for the slider
 * @param {number[]} range - Array of current slider values
 * @param {Function} onDelete - Callback function when delete button is clicked
 * @param {Function} onSliderChange - Callback function when slider value changes
 * @param {Function} onLabelChange - Callback function when label selection changes
 * @param {Function} onChangeCommitted - Callback function when slider change is committed
 * @param {boolean} [uncovered=false] - Whether the slider is in uncovered mode (read-only)
 * @param {boolean} [isDeletable=false] - Whether the configuration can be deleted
 * @param {number} [step=1] - Step increment for the slider
 * @returns {ReactElement} A configuration slider component
 */
export function ConfigurationSlider(props: Readonly<ConfigurationsSliderProps>): ReactElement {
  // Destructure props with default values
  const {
    label,
    min,
    max,
    range,
    onDelete,
    onSliderChange,
    onLabelChange,
    onChangeCommitted,
    uncovered = false,
    isDeletable = false,
    step = 1,
  } = props

  // Get current project from Redux store
  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const theme = getTheme()
  const { t } = useTranslation("ModelConfigurations")

  // State for search input in label selector
  const [searchKey, setSearchKey] = useState<string | undefined>("")

  // Fetch labels with infinite scrolling support
  const {
    isLoading: isLabelListLoading,
    isFetching: isLabelListFetching,
    data: labelList,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedLabelList>, AxiosError>(
    ["label-list", currentProject?.uuid, searchKey],
    ({ pageParam = 1 }) =>
      KonanAPI.RetrieveLabels({
        project_uuid: currentProject?.uuid as string,
        page: pageParam,
        pageSize: 20,
        search: searchKey,
      }),
    {
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5, // Data will be considered fresh for 5 minutes
      cacheTime: 1000 * 60 * 30,
      // Extract next page number from pagination link
      getNextPageParam: (lastPage) =>
        lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false,
      enabled: !!currentProject?.uuid,
    },
  )

  // Mutation for creating new labels
  const createLabelMutation = useMutation<AxiosResponse<Label>, AxiosError, { project_uuid: string; name: string }>(
    KonanAPI.CreateLabel,
    {
      onSuccess: async ({ data }) => {
        setSearchKey(data.name)
        await queryClient.invalidateQueries("label-list")

        NotificationUtils.toast(t("configurationSlider.toasts.labelCreateSuccess"), { severity: "success" })

        onLabelChange?.({ name: data.name, uuid: data.uuid })
      },
      onError: () => {
        onLabelChange?.({ name: null, uuid: null })

        NotificationUtils.toast(t("configurationSlider.toasts.labelCreateError"), { severity: "error" })
      },
    },
  )

  // Transform the paginated label data into a flat list for the dropdown
  const adjustedLabelsList = useMemo(
    () =>
      labelList?.pages.reduce(
        (accumulator, page) =>
          accumulator.concat(
            page.data?.results?.map((label: Label) => ({ label: label.name, value: label.uuid })) ?? [],
          ),
        [] as Array<{ label: string; value: string }>,
      ),
    [labelList?.pages],
  )

  const debouncedOnChange = useDebounce(setSearchKey, 400)

  // Handle changes in the label selector
  const handleOnChange = (e: InputChangeEvent<Element>): void => {
    // Handle click selection
    // @ts-expect-error
    if (e._reactName === "onClick") onLabelChange?.({ name: e.target.label as string, uuid: e.target.value as string })
    else {
      // If exact match is found, select that label
      const targetLabel = adjustedLabelsList?.find(
        (label) => label.label.toLowerCase() === e.target.value.toString().toLowerCase(),
      )

      if (targetLabel) onLabelChange?.({ name: targetLabel.label, uuid: targetLabel.value })
      else {
        onLabelChange?.({ name: e.target.value as string, uuid: null })

        debouncedOnChange(e.target.value as string)
      }
    }
  }

  // Handle creation of new labels
  const handleAddNewValue = async (value: string): Promise<void> => {
    await createLabelMutation.mutateAsync({
      project_uuid: currentProject?.uuid as string,
      name: value,
    })
  }

  return (
    <Grid container direction="column" gap={2} className={styles.sliderBox}>
      {/* Label selector section */}
      <Grid container item justifyContent="space-between">
        <Grid item key={`${labelList?.pages.length}`}>
          {!uncovered ? (
            <InputText
              optionsWithValues={adjustedLabelsList}
              value={label.name ?? searchKey}
              placeholder={t("configurationSlider.placeholder.selectLabel")}
              fullWidth
              hideDescription
              handleChange={handleOnChange}
              menuProps={{
                menuMaxContent: true,
                scrollThreshold: 10,
                handleScrollToEnd: () => {
                  hasNextPage && !isFetchingNextPage && fetchNextPage()
                },
              }}
              loaderComponent={
                <Grid container item xs={12} px={2} py={0.5}>
                  <CircularProgress size={14} />
                </Grid>
              }
              noEntriesProps={{
                shouldAcceptNewValues: true,
                handleAddNewValue,
              }}
              isLoadingOptions={isLabelListFetching || isLabelListLoading}
              loading={isLabelListLoading || createLabelMutation.isLoading}
            />
          ) : (
            <Typography variant="p">{label.name}</Typography>
          )}
        </Grid>

        {/* Delete button */}
        <Grid container item xs={1} justifyContent="flex-end">
          {!isDeletable && !uncovered && (
            <IconButton
              edge="end"
              color="inherit"
              onClick={onDelete}
              aria-label={t("configurationSlider.aria.delete")}
              className={styles.closeBtn}
              size="large"
            >
              <CloseIcon color="action" style={{ color: theme.palette.gray.background[1] }} />
            </IconButton>
          )}
        </Grid>
      </Grid>

      {/* Slider section */}
      <Grid container item>
        {range.map((range) => (
          <Grid item xs={12} key={`slider-${label}`}>
            <BaseSlider
              variant="single"
              sliderProps={{
                onChange: (_e: Event, value: number | number[]) => {
                  onSliderChange?.(value as number[])
                },
                onChangeCommitted: (_e: SyntheticEvent | Event, value: number | number[]) => {
                  onChangeCommitted?.(value as number[])
                },
                value: range,
                disabled: uncovered,
                step,
                min,
                max,
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  )
}
